import { BreakpointObserver } from '@angular/cdk/layout';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input, OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'app-search-box',
    templateUrl: './search-box.component.html',
    styleUrls: ['./search-box.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SearchBoxComponent implements OnInit, OnDestroy {
    private _searchVisible = new BehaviorSubject<boolean>(false);

    private searchListener = new BehaviorSubject<string>('');
    private defaultOpenOnDesktopListener = new BehaviorSubject<boolean>(true);

    private destroy$ = new Subject<void>();

    public isMobile$: Observable<boolean>;
    public isSearchVisible$: Observable<boolean>;
    public isSearchOpened$: Observable<boolean>;

    @Input() backIcon = 'arrow_back';
    @Input() searchIcon = 'search';
    @Input() placeholder = 'Procurar';
    @Input() showUnderline = true;
    @Input() debounceTime = 300;
    @Output() searchDebounce = new EventEmitter<string>();
    @Output() clear = new EventEmitter<boolean>();
    @Output() searchOpened = new EventEmitter<void>();
    @Output() searchClosed = new EventEmitter<void>();

    @ViewChild('searchInput', { static: false }) input: ElementRef;

    constructor(
        private changeDetector: ChangeDetectorRef,
        private breakpointObserver: BreakpointObserver
    ) {
        this.isMobile$ = this.breakpointObserver.observe(['(max-width: 768px)']).pipe(
            map((v) => v.matches),
            takeUntil(this.destroy$)
        );
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        this.searchListener
            .asObservable()
            .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe((v) => {
                this.searchDebounce.emit(v);
            });

        this.isSearchOpened$ = combineLatest([
            this.defaultOpenOnDesktopListener,
            this.isMobile$
        ]).pipe(
            map(([defaultOpenOnDesktop, isMobile]) => defaultOpenOnDesktop && !isMobile),
            tap(() => this.changeDetector.markForCheck()),
            takeUntil(this.destroy$)
        );

        this.isSearchVisible$ = this._searchVisible;

        setTimeout(() => {
            this.changeDetector.markForCheck();
        }, 100);
    }

    @Input()
    set searchVisible(value: boolean) {
        this._searchVisible.next(value);
        if (!this.searchVisible) {
            this.clearSearch();
        }
    }

    get searchVisible() {
        return this._searchVisible.value;
    }

    @Input()
    set defaultOpenOnDesktop(value: boolean) {
        this.defaultOpenOnDesktopListener.next(value);
    }

    get defaultOpenOnDesktop() {
        return this.defaultOpenOnDesktopListener.value;
    }

    toggleSearch() {
        this.searchVisible = !this.searchVisible;
    }

    openSearch() {
        this.searchVisible = true;
        this.searchOpened.emit();
    }

    closeSearch() {
        this.searchVisible = false;
        this.searchClosed.emit();
    }

    clearSearch() {
        this.searchDebounce.emit((this.input.nativeElement.value = ''));
    }

    onSearchValueChange() {
        this.searchListener.next(this.input.nativeElement.value);
    }

    focusOnInput() {
        this.input?.nativeElement?.focus();
    }
}
